import {
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type TextInputKeyPressEvent,
    type ViewStyle,
    StyleSheet,
    Platform,
    View
} from "react-native";
import type IComposerProps from "./type";
import type {
    ComposerContextType,
    ComposerAttachment
} from "./type";
import stylesheet, {
    COMPOSER_INPUT_GROUP_SIZES,
    COMPOSER_COUNTER_RATIO,
    COMPOSER_HINT_ICONS,
    useStyles
} from "./stylesheet";
import ComposerActions from "./components/actions";
import ComposerAttachments from "./components/attachments";
import {
    ComposerPaperclipIcon
} from "./components/icons";
import ComposerInput from "./components/input";
import ComposerSubmit from "./components/submit";
import {
    ComposerContext
} from "./context";
import ComposerAttachmentRows from "./internal/attachmentRows";
import {
    getComposerAttachmentFromFile,
    getComposerIsEnterSubmit,
    getComposerPendingState,
    getComposerIsSubmitKey,
    getComposerSlots,
    getComposerHint,
    getComposerSize
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    usePresence
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    TextAreaKeyPressEventData
} from "../inputGroup/components/textArea/type";
import {
    Upload as UploadIcon
} from "lucide-react-native";
import {
    INPUT_GROUP_COMPACT_SIZES
} from "../inputGroup/stylesheet";
import {
    uuid
} from "../../utils";
import AnimatedIcon from "../animatedIcon";
import Attachment from "../attachment";
import InputGroup from "../inputGroup";
import StateLayer from "../stateLayer";
import Text from "../text";

const Composer: FC<IComposerProps> = ({
    onChangeText: onChangeTextProp,
    submitBehaviour = "auto",
    isStreaming = false,
    onAttachmentsChange,
    isDisabled = false,
    defaultValue = "",
    isOffline = false,
    onAttachmentPress,
    attachments = [],
    value: valueProp,
    onEditLastPress,
    customLocalize,
    maxAttachments,
    onAttachPress,
    disabledText,
    customTheme,
    maxRows = 8,
    onStopPress,
    placeholder,
    maxLength,
    children,
    onSubmit,
    accept,
    style,
    title,
    size,
    ...props
}) => {
    const {
        shapeConfigs,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const containerRef = useRef<View>(null);

    const isMountedRef = useRef(false);

    const dragDepthRef = useRef(0);

    const [
        textState,
        setTextState
    ] = useState(defaultValue);

    const [
        isDragOver,
        setIsDragOver
    ] = useState(false);

    const {
        container: containerDynamicStyle,
        dropLayer: dropLayerDynamicStyle
    } = useStyles({
        isWidthControlled: StyleSheet.flatten<ViewStyle>(style)?.width !== undefined,
        shapeConfigs,
        radiuses,
        borders,
        spaces,
        colors
    });

    const currentTitle = title ?? localize("message");

    const currentText = valueProp ?? textState;

    const currentSize = getComposerSize({
        pointerType,
        size
    });

    const inputGroupSize = COMPOSER_INPUT_GROUP_SIZES[currentSize];

    const buttonSize = INPUT_GROUP_COMPACT_SIZES[inputGroupSize] === "small" ? "small" : "medium";

    const slots = getComposerSlots({
        children
    });

    const {
        isPending,
        isFailed
    } = getComposerPendingState({
        attachments
    });

    const isAttachmentsVisible = attachments.length > 0 || !!slots.attachments?.props.children;

    const {
        isRendered: isAttachmentsRendered
    } = usePresence({
        isVisible: isAttachmentsVisible,
        exitIntent: "listRemove",
        customTheme
    });

    const isOverLimit = !!maxLength && currentText.length > maxLength;

    const isEmpty = currentText.trim() === "" && attachments.length === 0;

    const canSubmit = !isDisabled && !isStreaming && !isOverLimit && !isPending && !isFailed && !isEmpty;

    const isEnterSubmit = getComposerIsEnterSubmit({
        submitBehaviour,
        pointerType
    });

    const inputHint = slots.input?.props.accessibilityHint ?? (isEnterSubmit ? localize("composer-keyboard-hint") : undefined);

    const isFileDropEnabled = !!onAttachmentsChange && !isDisabled;

    const isAttachShown = !!onAttachPress || (Platform.OS === "web" && !!onAttachmentsChange);

    const currentHint = getComposerHint({
        disabledText,
        isDisabled,
        isOffline,
        isPending,
        isFailed,
        localize
    });

    const handleFilesAdd = (files: Array<File>) => {
        if(!onAttachmentsChange || isDisabled || files.length === 0) {
            return;
        }

        const available = maxAttachments === undefined ? files.length : Math.max(maxAttachments - attachments.length, 0);

        const addedAttachments = files.slice(0, available).map((file) => getComposerAttachmentFromFile({
            locale: activeLocale,
            id: uuid(),
            file
        }));

        if(addedAttachments.length === 0) {
            return;
        }

        onAttachmentsChange([
            ...attachments,
            ...addedAttachments
        ]);
    };

    const latestRef = useRef({
        isFileDropEnabled,
        handleFilesAdd
    });

    latestRef.current = {
        isFileDropEnabled,
        handleFilesAdd
    };

    useEffect(() => {
        isMountedRef.current = true;
    }, []);

    useEffect(() => {
        const node = containerRef.current;

        if(Platform.OS !== "web" || typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
            return;
        }

        const getHasFiles = (event: DragEvent) => {
            return !!event.dataTransfer && Array.from(event.dataTransfer.types).includes("Files");
        };

        const onDragEnter = (event: DragEvent) => {
            if(!latestRef.current.isFileDropEnabled || !getHasFiles(event)) {
                return;
            }

            event.preventDefault();

            dragDepthRef.current += 1;

            setIsDragOver(true);
        };

        const onDragOver = (event: DragEvent) => {
            if(!latestRef.current.isFileDropEnabled || !getHasFiles(event)) {
                return;
            }

            event.preventDefault();
        };

        const onDragLeave = (event: DragEvent) => {
            if(!getHasFiles(event)) {
                return;
            }

            dragDepthRef.current = Math.max(dragDepthRef.current - 1, 0);

            if(dragDepthRef.current === 0) {
                setIsDragOver(false);
            }
        };

        const onDrop = (event: DragEvent) => {
            if(!getHasFiles(event)) {
                return;
            }

            event.preventDefault();

            dragDepthRef.current = 0;

            setIsDragOver(false);

            if(latestRef.current.isFileDropEnabled) {
                latestRef.current.handleFilesAdd(Array.from(event.dataTransfer?.files ?? []));
            }
        };

        const onPaste = (event: ClipboardEvent) => {
            const files = Array.from(event.clipboardData?.files ?? []);

            if(!latestRef.current.isFileDropEnabled || files.length === 0) {
                return;
            }

            event.preventDefault();

            latestRef.current.handleFilesAdd(files);
        };

        node.addEventListener("dragenter", onDragEnter);
        node.addEventListener("dragover", onDragOver);
        node.addEventListener("dragleave", onDragLeave);
        node.addEventListener("drop", onDrop);
        node.addEventListener("paste", onPaste);

        return () => {
            node.removeEventListener("dragenter", onDragEnter);
            node.removeEventListener("dragover", onDragOver);
            node.removeEventListener("dragleave", onDragLeave);
            node.removeEventListener("drop", onDrop);
            node.removeEventListener("paste", onPaste);
        };
    }, []);

    const handleChangeText = (text: string) => {
        setTextState(text);

        if(onChangeTextProp) {
            onChangeTextProp(text);
        }
    };

    const handleSubmit = useCallback(() => {
        if(!canSubmit) {
            return;
        }

        if(onSubmit) {
            onSubmit(currentText.trim(), attachments);
        }

        if(valueProp === undefined) {
            setTextState("");
        }
    }, [
        currentText,
        attachments,
        valueProp,
        canSubmit,
        onSubmit
    ]);

    const handleAttachPress = () => {
        if(onAttachPress) {
            onAttachPress();

            return;
        }

        if(Platform.OS !== "web") {
            return;
        }

        const input = globalThis.document.createElement("input");

        input.type = "file";

        input.multiple = true;

        if(accept) {
            input.accept = accept;
        }

        input.addEventListener("change", () => {
            handleFilesAdd(Array.from(input.files ?? []));
        });

        input.click();
    };

    const handleAttachmentRemove = (id: string) => {
        if(onAttachmentsChange) {
            onAttachmentsChange(attachments.filter((item) => item.id !== id));
        }
    };

    const onKeyPress = (event: TextInputKeyPressEvent) => {
        if(slots.input?.props.onKeyPress) {
            slots.input.props.onKeyPress(event);
        }

        if(Platform.OS !== "web") {
            return;
        }

        const eventData = event.nativeEvent as TextAreaKeyPressEventData;

        if(eventData.key === "Escape") {
            if(isStreaming && onStopPress) {
                event.preventDefault();

                onStopPress();
            }

            return;
        }

        if(eventData.key === "ArrowUp" && currentText === "" && onEditLastPress) {
            event.preventDefault();

            onEditLastPress();

            return;
        }

        if(getComposerIsSubmitKey({
            event: eventData,
            isEnterSubmit
        })) {
            event.preventDefault();

            handleSubmit();
        }
    };

    const renderAttachment = (item: ComposerAttachment) => {
        const attachmentProps: Partial<ComposerAttachment> = {
            ...item
        };

        delete attachmentProps.file;

        delete attachmentProps.id;

        return <Attachment
            {...attachmentProps}
            onPress={onAttachmentPress ? () => onAttachmentPress(item) : undefined}
            size={currentSize === "large" ? "medium" : "small"}
            onRemove={() => handleAttachmentRemove(item.id)}
            customLocalize={customLocalize}
            customTheme={customTheme}
            key={item.id}
        />;
    };

    const renderAttachments = () => {
        if(!isAttachmentsRendered) {
            return null;
        }

        if(!slots.attachments?.props.layoutBehaviour && !slots.attachments?.props.children) {
            return <InputGroup.Addon
                align="blockStart"
                key="attachments"
            >
                <ComposerAttachmentRows
                    customTheme={slots.attachments?.props.customTheme ?? customTheme}
                    isInitialAnimated={isMountedRef.current}
                    renderAttachment={renderAttachment}
                    style={[
                        slots.attachments?.props.style,
                        stylesheet.attachments
                    ]}
                    customLocalize={customLocalize}
                    title={localize("attachments")}
                    attachments={attachments}
                />
            </InputGroup.Addon>;
        }

        return <InputGroup.Addon
            align="blockStart"
            key="attachments"
        >
            <Attachment.Group
                customTheme={slots.attachments?.props.customTheme ?? customTheme}
                layoutBehaviour={slots.attachments?.props.layoutBehaviour}
                isInitialAnimated={isMountedRef.current}
                style={[
                    slots.attachments?.props.style,
                    stylesheet.attachments
                ]}
                customLocalize={customLocalize}
                title={localize("attachments")}
                isInField={true}
            >
                {slots.attachments?.props.children ?? attachments.map(renderAttachment)}
            </Attachment.Group>
        </InputGroup.Addon>;
    };

    const renderInput = () => {
        const inputProps = slots.input?.props;

        const isInputNamed = !!inputProps?.["aria-label"] || !!inputProps?.["aria-labelledby"] || !!inputProps?.accessibilityLabel;

        return <InputGroup.TextArea
            {...inputProps}
            placeholder={inputProps?.placeholder ?? placeholder ?? localize("message-placeholder")}
            aria-label={isInputNamed ? inputProps?.["aria-label"] : currentTitle}
            onChangeText={handleChangeText}
            accessibilityHint={inputHint}
            onKeyPress={onKeyPress}
            submitBehaviour="none"
            value={currentText}
            maxRows={maxRows}
            key="input"
            minRows={1}
        />;
    };

    const renderKeyboardHint = () => {
        if(Platform.OS !== "web" || !inputHint) {
            return null;
        }

        return <InputGroup.Text
            style={stylesheet.keyboardHint}
            aria-hidden={true}
        >
            {inputHint}
        </InputGroup.Text>;
    };

    const renderToolbar = () => {
        return <InputGroup.Addon
            align="blockEnd"
            key="toolbar"
        >
            {
                isAttachShown ?
                    <InputGroup.Button
                        title={localize("attach-file")}
                        icon={ComposerPaperclipIcon}
                        onPress={handleAttachPress}
                        key="attach"
                    />
                :
                    null
            }
            {slots.actions}
            {slots.submit ?? <ComposerSubmit
                key="submit"
            />}
            {renderKeyboardHint()}
        </InputGroup.Addon>;
    };

    const renderDropLayer = () => {
        if(!isDragOver) {
            return null;
        }

        return <View
            style={[
                stylesheet.dropLayer,
                dropLayerDynamicStyle
            ]}
        >
            <StateLayer
                borderRadius={radiuses.form}
                borderWidth={borders.line}
                customTheme={customTheme}
                overlayColor="default"
                isPressed={false}
                isHovered={true}
            />
            <AnimatedIcon
                customTheme={customTheme}
                animationTrigger="mount"
                size={iconSizes.medium}
                animation="signature"
                icon={UploadIcon}
                color="mid"
            />
            <Text
                customTheme={customTheme}
                variant="labelMediumSize"
                color="high"
            >
                {localize("drop-files")}
            </Text>
        </View>;
    };

    const contextValue = useMemo((): ComposerContextType => ({
        onSubmitPress: handleSubmit,
        isStreaming: isStreaming,
        customLocalize,
        customTheme,
        onStopPress,
        buttonSize,
        isDisabled,
        canSubmit
    }), [
        customLocalize,
        handleSubmit,
        onStopPress,
        customTheme,
        isStreaming,
        buttonSize,
        isDisabled,
        canSubmit
    ]);

    return <ComposerContext.Provider
        value={contextValue}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            ref={containerRef}
        >
            <InputGroup
                isShowCounter={!!maxLength && currentText.length >= maxLength * COMPOSER_COUNTER_RATIO}
                hintTextIcon={currentHint ? COMPOSER_HINT_ICONS[currentHint.icon] : undefined}
                isShowHintTextIcon={!!currentHint}
                customLocalize={customLocalize}
                hintText={currentHint?.text}
                aria-label={currentTitle}
                customTheme={customTheme}
                spreadBehaviour="stretch"
                isDisabled={isDisabled}
                size={inputGroupSize}
                maxLength={maxLength}
            >
                {renderAttachments()}
                {renderInput()}
                {renderToolbar()}
            </InputGroup>
            {renderDropLayer()}
        </View>
    </ComposerContext.Provider>;
};
export default Object.assign(Composer, {
    Attachments: ComposerAttachments,
    Action: InputGroup.Button,
    Actions: ComposerActions,
    Submit: ComposerSubmit,
    Input: ComposerInput
});
